import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import VerifiedBadge from "@/components/VerifiedBadge";
import CoverArt from "@/components/CoverArt";
import { Input } from "@/components/ui/input";
import { Search, Play, Loader2, Globe2, Music2 } from "lucide-react";
import { toast } from "sonner";

function FeedCard({ s }) {
  return (
    <Link
      to={`/song/${s.id}`}
      className="group block"
      data-testid={`feed-card-${s.id}`}
    >
      <div className="aspect-square w-full relative">
        <CoverArt song={s} className="w-full h-full" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none rounded-sm" />
        <div className="absolute bottom-3 right-3 h-10 w-10 rounded-full bg-[#FF3B30] flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all pointer-events-none">
          <Play className="h-4 w-4 fill-current text-white ml-0.5" />
        </div>
        {s.is_synced && (
          <span className="absolute top-2 left-2 font-mono text-[9px] uppercase tracking-[0.25em] text-[#FF3B30] border border-[#FF3B30]/60 px-1.5 py-0.5 rounded-sm bg-black/40 backdrop-blur">
            synced
          </span>
        )}
      </div>
      <div className="mt-3">
        <div className="font-display font-bold text-base truncate group-hover:text-white">
          {s.title}
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/40 mt-1 truncate">
          {s.artist || "unknown artist"}
        </div>
        <div className="mt-2 flex items-center gap-1.5 text-white/50 text-xs">
          <Globe2 className="h-3 w-3 text-[#FF3B30]" />
          <span className="truncate">
            shared by{" "}
            <span className="text-white/80 inline-flex items-center gap-1">
              {s.uploader_name || "Someone"}
              {s.uploader_verified && <VerifiedBadge size={12} />}
            </span>
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function Feed() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");

  const load = async (query = "") => {
    try {
      const { data } = await api.get("/feed", {
        params: query ? { q: query } : {},
      });
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load feed");
      setItems([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 lg:px-12 pt-10 pb-32">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] flex items-center gap-2">
              <Globe2 className="h-3 w-3" /> // public feed
            </div>
            <h1 className="mt-2 font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              What the world <br />
              <span className="text-white/30">is singing.</span>
            </h1>
            <p className="mt-3 text-white/50 text-sm max-w-md">
              Open lyrics — synced, scrolling, in time with the song. Anyone can listen, only
              uploaders can publish.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search title, artist, sharer"
                className="pl-9 w-72 bg-transparent border-white/10 focus-visible:border-[#FF3B30] focus-visible:ring-0 rounded-sm h-11"
                data-testid="feed-search-input"
              />
            </div>
          </div>
        </div>

        {items === null ? (
          <div className="py-32 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-white/40" />
          </div>
        ) : items.length === 0 ? (
          <div
            className="border border-white/10 rounded-sm p-12 text-center bg-[#0F0F0F]"
            data-testid="feed-empty"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30]">
              // empty stage
            </div>
            <h2 className="mt-3 font-display font-black text-4xl tracking-tighter">
              No public tracks yet.
            </h2>
            <p className="mt-3 text-white/50 max-w-md mx-auto">
              Upload a song from your library and toggle it public — it'll appear here for the
              whole community to play and sing along.
            </p>
          </div>
        ) : (
          <div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6"
            data-testid="feed-grid"
          >
            {items.map((s) => (
              <FeedCard key={s.id} s={s} />
            ))}
          </div>
        )}

        <div className="mt-16 flex items-center gap-3 text-white/30">
          <Music2 className="h-4 w-4" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em]">
            // {items?.length || 0} public track{(items?.length || 0) === 1 ? "" : "s"}
          </span>
        </div>
      </div>
    </Layout>
  );
}
